/**
 * Modern Notification Button Component
 *
 * A beautiful, shadcn-styled notification button for the header.
 * Features:
 * - Subtle indicator dot when there are unread notifications
 * - Connection status indicator
 * - Click to navigate to notifications page (Facebook-style)
 * - Clean and minimal design
 */

import { Bell } from "lucide-react";
import { Link } from "react-router";
import { useUnreadCount } from "../hooks/useNotifications";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

interface NotificationButtonProps {
  className?: string;
}

export default function NotificationButton({ className }: NotificationButtonProps) {
  const { unreadCount, isSSEConnected } = useUnreadCount();
  const hasUnread = unreadCount > 0;

  const getStatusMessage = () => {
    if (!isSSEConnected) {
      return 'Connection lost';
    }

    if (!hasUnread) {
      return 'No new notifications';
    }

    return 'You have new notifications';
  };

  return (
    <Link to="/notifications" className={cn("relative", className)}>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-9 w-9 hover:bg-accent/50 transition-all duration-200"
        title={getStatusMessage()}
        aria-label={`Notifications. ${getStatusMessage()}`}
      >
        <Bell className={cn(
          "h-5 w-5 transition-all duration-200",
          hasUnread ? "text-primary" : "text-muted-foreground"
        )} />

        {/* Unread indicator - subtle dot at top right */}
        {hasUnread && (
          <span
            className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary animate-in zoom-in-50 duration-200"
            title="You have unread notifications"
          />
        )}

        {/* Connection status indicator - small dot at bottom right */}
        <span
          className={cn(
            "absolute bottom-0.5 right-0.5 h-1.5 w-1.5 rounded-full border border-background transition-all duration-300",
            isSSEConnected ? "bg-green-500" : "bg-red-500 animate-pulse"
          )}
          title={isSSEConnected ? "Connected" : "Disconnected"}
        />
      </Button>
    </Link>
  );
}
